import * as THREE from 'three';
import { getGrid } from '../../store/LoadingStore';

export class GeometryUtils {
    /**
     * Projects a 3D position onto the ground surface using raycasting. Handles coordinate system
     * transformation between the scene's rotated coordinate system (x, -z, y) and the original system (x, y, z) needed
     * for raycasting operations. Includes fallback upward casting if downward ray misses.
     *
     * @param {THREE.Vector3} position - Input position in transformed coordinate system
     * @param {THREE.Raycaster} raycaster - THREE.js raycaster instance for intersection testing
     * @returns {THREE.Vector3} Projected position on ground surface in transformed coordinates
     */
    static projectToGround(position: THREE.Vector3, raycaster: THREE.Raycaster): THREE.Vector3 {
        const ground: THREE.Object3D[] = getGrid().getActiveGeodata().dataObjectLegacy?.Ground?.trianglesMesh.children ?? [];

        // Rotated to OG: (x,y,z) of Rotated -> (x, z, -y)
        const originalPosition = new THREE.Vector3(position.x, position.z, -position.y);

        // Downward raycasting
        const rayOrigin = new THREE.Vector3(originalPosition.x, originalPosition.y + 1000, originalPosition.z);
        const rayDirection = new THREE.Vector3(0, -1, 0);
        raycaster.set(rayOrigin, rayDirection);
        const intersects = raycaster.intersectObjects(ground, true);

        if (intersects.length > 0) {
            const intersection = intersects[0];
            // OG to Rotated: (x,y,z) of OG -> (x, -z, y)
            return new THREE.Vector3(intersection.point.x, -intersection.point.z, intersection.point.y);
        }

        // Fallback: Upward raycasting
        const rayOriginBelow = new THREE.Vector3(originalPosition.x, originalPosition.y - 1000, originalPosition.z);
        const rayDirectionUp = new THREE.Vector3(0, 1, 0);
        raycaster.set(rayOriginBelow, rayDirectionUp);
        const intersectsUp = raycaster.intersectObjects(ground, true);

        if (intersectsUp.length > 0) {
            const intersection = intersectsUp[0];
            // OG to Rotated: (x,y,z) of OG -> (x, -z, y)
            return new THREE.Vector3(intersection.point.x, -intersection.point.z, intersection.point.y);
        }

        return position;
    }

    /**
     * Calculates the distance from a point to a line segment.
     *
     * @param {THREE.Vector3} point - The point to measure from
     * @param {THREE.Vector3} lineStart - Start of the line segment
     * @param {THREE.Vector3} lineEnd - End of the line segment
     * @returns {number} Distance from point to line segment
     */
    static distanceToLineSegment(point: THREE.Vector3, lineStart: THREE.Vector3, lineEnd: THREE.Vector3): number {
        const line = new THREE.Vector3().subVectors(lineEnd, lineStart);
        const lineLength = line.length();

        if (lineLength === 0) return point.distanceTo(lineStart);

        const t = Math.max(0, Math.min(1, new THREE.Vector3().subVectors(point, lineStart).dot(line) / (lineLength * lineLength)));
        const projection = new THREE.Vector3().addVectors(lineStart, line.multiplyScalar(t));

        return point.distanceTo(projection);
    }

    /**
     * Projects a 2D point onto the infinite line AB and returns the normalized line parameter t.
     *
     * t = 0 maps to A, t = 1 maps to B, and values outside [0, 1] lie beyond the segment.
     *
     * @param point - Point to project
     * @param a - Line start point
     * @param b - Line end point
     * @returns Normalized parameter along AB
     */
    static projectPointToLineParam2D(point: THREE.Vector2, a: THREE.Vector2, b: THREE.Vector2): number {
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const lenSq = dx * dx + dy * dy;
        if (lenSq <= 1e-12) return 0;
        return ((point.x - a.x) * dx + (point.y - a.y) * dy) / lenSq;
    }

    static projectPointToInfiniteLine2D(point: THREE.Vector2, a: THREE.Vector2, b: THREE.Vector2): THREE.Vector2 {
        const t = GeometryUtils.projectPointToLineParam2D(point, a, b);
        return new THREE.Vector2(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t);
    }

    static isPointOnSegment2D(point: THREE.Vector2, a: THREE.Vector2, b: THREE.Vector2, eps = 1e-6): boolean {
        const abx = b.x - a.x;
        const aby = b.y - a.y;
        const apx = point.x - a.x;
        const apy = point.y - a.y;
        const abLenSq = abx * abx + aby * aby;
        if (abLenSq <= eps) return false;

        const cross = Math.abs(apx * aby - apy * abx);
        if (cross > eps) return false;

        const dot = apx * abx + apy * aby;
        if (dot < -eps) return false;
        if (dot > abLenSq + eps) return false;
        return true;
    }

    /**
     * Calculates the perpendicular distance from a 2D point to the infinite line AB.
     *
     * @param point - Point to measure from
     * @param a - Line start point
     * @param b - Line end point
     * @returns Perpendicular distance, or Infinity for degenerate AB
     */
    static distanceToInfiniteLine2D(point: THREE.Vector2, a: THREE.Vector2, b: THREE.Vector2): number {
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const len = Math.hypot(dx, dy);
        if (len <= 1e-12) return Infinity;
        const cross = Math.abs(dx * (point.y - a.y) - dy * (point.x - a.x));
        return cross / len;
    }

    /**
     * Finds the closest line segment to the click point within a threshold distance.
     *
     * @param {THREE.Vector3} clickPoint - The clicked position
     * @param {THREE.Vector3[]} points - Array of points forming the polyline
     * @param {number} threshold - Maximum distance threshold for detection
     * @returns {Object|null} Object with segmentIndex and distance, or null if no line is close enough
     */
    static findLineSegmentNearClick(
        clickPoint: THREE.Vector3,
        points: THREE.Vector3[],
        threshold: number
    ): { segmentIndex: number; distance: number } | null {
        let closestSegment: { segmentIndex: number; distance: number } | null = null;

        for (let i = 0; i < points.length; i++) {
            const currentPoint = points[i];
            const nextPoint = points[(i + 1) % points.length];

            // Calculate distance from click point to line segment
            const distance = GeometryUtils.distanceToLineSegment(clickPoint, currentPoint, nextPoint);

            if (distance < threshold) {
                closestSegment = { segmentIndex: i, distance };
            }
        }

        return closestSegment;
    }

    /**
     * Finds a point near the click location within a threshold distance.
     *
     * @param {THREE.Vector3} clickPoint - The clicked position
     * @param {THREE.Vector3[]} points - Array of points to search through
     * @param {number} threshold - Maximum distance threshold for detection
     * @returns {number|null} Index of the nearest point, or null if none found
     */
    static findPointNearClick(clickPoint: THREE.Vector3, points: THREE.Vector3[], threshold: number): number | null {
        for (let i = 0; i < points.length; i++) {
            const distance = clickPoint.distanceTo(points[i]);
            if (distance <= threshold) {
                return i;
            }
        }
        return null;
    }

    /**
     * Finds the nearest point to the click, specifically checking the first point during drawing mode.
     *
     * @param {THREE.Vector3} clickPoint - The clicked position
     * @param {THREE.Vector3[]} points - Array of points to search through
     * @param {number} closeRadius - Radius for closing the polyline
     * @param {number} minNumPoints - Minimum number of points required
     * @returns {number|null} Index of the nearest point (always 0 if within range), or null
     */
    static findNearestPoint(clickPoint: THREE.Vector3, points: THREE.Vector3[], closeRadius: number, minNumPoints: number): number | null {
        if (points.length < minNumPoints) return null;

        // Only check distance to the first point (index 0) during drawing
        const distance = clickPoint.distanceTo(points[0]);
        if (distance <= closeRadius) return 0;

        return null;
    }

    /**
     * Converts an array of Vector3 points to a flat array of coordinates.
     *
     * @param {THREE.Vector3[]} points - Array of Vector3 points
     * @returns {number[]} Flat array of [x, y, z, x, y, z, ...] coordinates
     */
    static getPositionsArray(points: THREE.Vector3[]): number[] {
        return points.flatMap((p) => [p.x, p.y, p.z]);
    }

    /**
     * Calculates the midpoint between two Vector3 points.
     *
     * @param {THREE.Vector3} point1 - First point
     * @param {THREE.Vector3} point2 - Second point
     * @returns {THREE.Vector3} Midpoint between the two points
     */
    static getMidpoint(point1: THREE.Vector3, point2: THREE.Vector3): THREE.Vector3 {
        return new THREE.Vector3().addVectors(point1, point2).multiplyScalar(0.5);
    }

    /**
     * Checks if a point is within the GUI column areas (left or right).
     *
     * @param {number} clientX - X coordinate of the mouse click
     * @param {number} columnWidth - Width of the GUI columns
     * @param {number} windowWidth - Width of the window
     * @returns {boolean} True if the point is within GUI areas
     */
    static isPointInGUIArea(clientX: number, columnWidth: number, windowWidth: number): boolean {
        return columnWidth > clientX || clientX > windowWidth - columnWidth;
    }

    /**
     * Computes the area of a polygon using the Shoelace formula.
     *
     * @param legalBorder - Map with keys 'x' and 'y', each an array of numbers representing polygon vertices
     * @returns The polygon area
     */
    static computeAreaFromBorder(legalBorder: Map<string, number[]>): number {
        const xs = legalBorder.get('x');
        const ys = legalBorder.get('y');

        if (!xs || !ys || xs.length !== ys.length || xs.length < 3) {
            throw new Error('Invalid polygon data');
        }

        const n = xs.length;
        let area = 0;

        for (let i = 0; i < n; i++) {
            const j = (i + 1) % n;
            area += xs[i] * ys[j] - xs[j] * ys[i];
        }

        return Math.abs(area) / 2;
    }

    /**
     * Computes the intersection point of two 2D lines defined by (p1–p2) and (p3–p4).
     *
     * Returns the intersection as a `THREE.Vector2`, or `null` if the lines are
     * parallel or coincident. The calculation is performed in the XY plane using
     * the standard determinant-based formula.
     *
     * @param p1 - First point of the first line
     * @param p2 - Second point of the first line
     * @param p3 - First point of the second line
     * @param p4 - Second point of the second line
     * @returns Intersection point as `THREE.Vector2`, or `null` if no unique intersection exists
     */
    static intersect2D(p1: THREE.Vector2, p2: THREE.Vector2, p3: THREE.Vector2, p4: THREE.Vector2): THREE.Vector2 | null {
        const x1 = p1.x,
            y1 = p1.y;
        const x2 = p2.x,
            y2 = p2.y;
        const x3 = p3.x,
            y3 = p3.y;
        const x4 = p4.x,
            y4 = p4.y;

        const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
        if (Math.abs(denom) < 1e-8) return null;

        const px = ((x1 * y2 - y1 * x2) * (x3 - x4) - (x1 - x2) * (x3 * y4 - y3 * x4)) / denom;
        const py = ((x1 * y2 - y1 * x2) * (y3 - y4) - (y1 - y2) * (x3 * y4 - y3 * x4)) / denom;

        return new THREE.Vector2(px, py);
    }

    static rayIntersectsSegment(origin: THREE.Vector2, dir: THREE.Vector2, a: THREE.Vector2, b: THREE.Vector2, eps = 1e-6): boolean {
        const v1 = origin.clone().sub(a);
        const v2 = b.clone().sub(a);
        const v3 = new THREE.Vector2(-dir.y, dir.x);

        const denom = v2.dot(v3);

        // If parallel no intersection:
        if (Math.abs(denom) < eps) return false;

        // distance along ray (t) and segment parameter (u):
        const t = v2.cross(v1) / denom;
        const u = v1.dot(v3) / denom;

        return t > eps && u > eps && u < 1 - eps;
    }

    static rayHitsPolygon(
        origin: THREE.Vector2,
        dir: THREE.Vector2,
        polygon: THREE.Vector2[],
        skipEdgeA: THREE.Vector2,
        skipEdgeB: THREE.Vector2
    ): boolean {
        const EPS_ORIGIN = 1e-4;
        const o = origin.clone().add(dir.clone().multiplyScalar(EPS_ORIGIN));

        for (let i = 0; i < polygon.length; i++) {
            const a = polygon[i];
            const b = polygon[(i + 1) % polygon.length];

            // Skip the facade edge itself:
            if ((a.equals(skipEdgeA) && b.equals(skipEdgeB)) || (a.equals(skipEdgeB) && b.equals(skipEdgeA))) {
                continue;
            }

            if (GeometryUtils.rayIntersectsSegment(o, dir, a, b)) {
                return true;
            }
        }
        return false;
    }

    static polygonIsCCW(poly: THREE.Vector2[]): boolean {
        let area = 0;
        for (let i = 0; i < poly.length; i++) {
            const a = poly[i];
            const b = poly[(i + 1) % poly.length];
            area += (b.x - a.x) * (b.y + a.y);
        }

        // CCW:
        return area < 0;
    }
}
